import { ChangeEventHandler, FormEventHandler, useEffect, useRef, useState } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import { Menu } from '~/modules/elements/akui/menu';
import Typography from '~/modules/elements/akui/primitives/typography';
import Modal from '~/modules/elements/modal';
import { transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import { GAME_CODE_LENGTH } from '~/modules/remote-mic/network/server/network-server';
import { ConnectionStatuses } from '~/routes/remote-mic/remote-mic';

interface Props {
  roomId: string | null;
  onConnect: (roomId: string) => void;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  autoConnect?: boolean;
}

const AUTO_TYPE_CHAR_DELAY_MS = 90;

const CELLS = Array.from({ length: GAME_CODE_LENGTH }, (_, index) => index);

export default function StepEnterCode({
  roomId,
  onConnect,
  connectionStatus,
  connectionError,
  autoConnect = true,
}: Props) {
  const [customRoomId, setCustomRoomId] = useState('');
  const [isAutoTyping, setIsAutoTyping] = useState(!!roomId);
  const [errorReset, setErrorReset] = useState(() => connectionStatus === 'error');
  const inputRef = useRef<HTMLInputElement>(null);
  const submittedCodeRef = useRef<string | null>(null);

  const isConnecting = connectionStatus === 'connecting' || connectionStatus === 'reconnecting';
  const disabled = isAutoTyping || (connectionStatus !== 'uninitialised' && connectionStatus !== 'error');

  useEffect(() => {
    if (!roomId) return;
    const gameCodeChars = roomId.toUpperCase().slice(0, GAME_CODE_LENGTH);
    let revealedCharCount = 0;
    const interval = setInterval(() => {
      revealedCharCount += 1;
      setCustomRoomId(gameCodeChars.slice(0, revealedCharCount));
      if (revealedCharCount >= gameCodeChars.length) {
        clearInterval(interval);
        setIsAutoTyping(false);
      }
    }, AUTO_TYPE_CHAR_DELAY_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run the reveal once, driven by the initial roomId only
  }, []);

  useEffect(() => {
    if (!isAutoTyping) inputRef.current?.focus();
  }, [isAutoTyping]);

  // connectionStatus is read as a gate here, not a dependency: adding it would re-fire the effect the
  // moment the status flips to 'connecting'.
  useEffect(() => {
    if (isAutoTyping || !autoConnect) return;
    if (
      customRoomId.length === GAME_CODE_LENGTH &&
      (connectionStatus === 'uninitialised' || connectionStatus === 'error') &&
      submittedCodeRef.current !== customRoomId
    ) {
      submittedCodeRef.current = customRoomId;
      onConnect(customRoomId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRoomId, isAutoTyping, autoConnect]);

  useEffect(() => {
    if (connectionStatus === 'error') submittedCodeRef.current = null;
  }, [connectionStatus]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    setErrorReset(false);
    if (customRoomId.length !== GAME_CODE_LENGTH) {
      inputRef.current?.focus();
      return;
    }
    if (submittedCodeRef.current === customRoomId) return;
    submittedCodeRef.current = customRoomId;
    onConnect(customRoomId);
  };

  const onCode: ChangeEventHandler<HTMLInputElement> = (event) => {
    setCustomRoomId(
      event.target.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, GAME_CODE_LENGTH),
    );
    if (connectionStatus === 'error') setErrorReset(true);
  };

  const shouldShowError = connectionStatus === 'error' && !errorReset;
  const complete = customRoomId.length === GAME_CODE_LENGTH;

  if (isConnecting) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5">
        <span
          aria-hidden
          className="border-t-active size-14 animate-spin rounded-full border-[0.3125rem] border-white/20"
        />
        <span className="text-[1.1875rem] font-bold">
          Connecting to <span data-test="connecting-game-code">{customRoomId.toUpperCase()}</span>…
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <span className="mt-5.5 text-[1.875rem] leading-[1.1] font-bold text-pretty">
          What&#39;s the code on the TV?
        </span>
        <span className="mt-2.5 text-[1rem] text-white/85">Five characters, under the QR code.</span>
        <form className="mt-auto flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="relative">
            {/* The cells are the drawing; the field over them owns the typing, so the phone's own
                keyboard, paste and caret all behave normally without five inputs to keep in step. */}
            <div className="pointer-events-none flex gap-2" aria-hidden>
              {CELLS.map((index) => {
                const char = customRoomId[index];
                const isNext = index === customRoomId.length;

                return (
                  <span
                    key={index}
                    className={`flex h-18 min-w-0 flex-1 items-center justify-center rounded-[0.875rem] text-[1.875rem] font-bold ${
                      char ? 'bg-black/55 text-white' : isNext ? 'text-active bg-black/35' : 'bg-black/35 text-white/25'
                    } ${isNext ? 'shadow-[inset_0_0_0_2px_#facc15]' : 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]'}`}>
                    {char ?? '_'}
                  </span>
                );
              })}
            </div>
            <input
              ref={inputRef}
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={GAME_CODE_LENGTH}
              value={customRoomId}
              onChange={onCode}
              disabled={disabled}
              aria-label="Game code"
              data-test="game-code-input"
              className="absolute inset-0 size-full border-none bg-transparent text-center text-[1.875rem] caret-transparent outline-none"
              style={{ color: 'transparent' }}
            />
          </div>
          <button
            type="submit"
            disabled={disabled}
            data-test="connect-button"
            className={`flex h-16 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold transition-[background-color,transform] duration-200 active:scale-[0.98] ${
              complete ? 'bg-active text-black' : 'bg-white/14 text-white/50'
            }`}>
            Connect
          </button>
        </form>
      </div>
      <Modal open={shouldShowError} onClose={() => setErrorReset(true)} data-test="connection-error-modal" withPortal>
        <Menu modal>
          <Menu.Header>Couldn&#39;t connect</Menu.Header>
          <Typography>
            {roomId ? (
              <>
                The game seems to be <strong>offline 🤔</strong>
              </>
            ) : (
              'Game with this code not found 🤔'
            )}
          </Typography>
          <Typography>
            <ul className="list-disc pl-12 text-sm leading-7">
              {!roomId && (
                <li>
                  Is the code <strong>{customRoomId.toUpperCase()}</strong> correct?
                </li>
              )}
              <li>
                Refresh (<strong>F5</strong>) the Karaoke Game on the PC.
              </li>
              {roomId && (
                <li className="inline-flex items-center gap-1">
                  Scan the{' '}
                  <strong className="inline-flex items-center gap-1">
                    <Icon icon="mdi:qrcode-scan" /> QR Code
                  </strong>{' '}
                  again
                </li>
              )}
              {/* Not "the same Wi-Fi" any more: this page is served from its own origin rather
                  than from the machine running the game, so the two meet on the relay and only need
                  to be online. Sending somebody to compare networks would be a wrong turn. */}
              <li className="inline-flex items-center gap-1">
                Check this phone is{' '}
                <strong className="inline-flex items-center gap-1">
                  <Icon icon="mdi:wifi" /> online
                </strong>
              </li>
            </ul>
          </Typography>
          <Typography className="text-sm">
            Error code: <strong>{connectionError}</strong>
          </Typography>
          <Menu.Button size="small" onClick={() => setErrorReset(true)}>
            Close
          </Menu.Button>
        </Menu>
      </Modal>
    </>
  );
}
